import { useQuery } from '@tanstack/react-query';
import dataService from 'services/dataService';

import { apiUrlGenerator } from '../use-api';

import type {
  ISuccessRateBucketCounts,
  ISuccessRateTiles,
  TPatientEngagementReportFilter,
} from './types';

const url = 'reports/reports/patient-engagement-status-report/tiles';

const emptyBucketCounts = (): ISuccessRateBucketCounts => ({
  first: 0,
  second: 0,
  thirdPlus: 0,
});

const emptyTiles = (): ISuccessRateTiles => ({
  callingQueueToOnboarding: emptyBucketCounts(),
  onboardingTwoWaySync: emptyBucketCounts(),
});

/**
 * The "Attempt Success Tracking" tiles. Scoped by Clinic/Provider/Child
 * Clinic/Status plus the Month picker, which only affects these tiles.
 */
export const useGetPatientEngagementSuccessRate = (
  filter: TPatientEngagementReportFilter,
) => {
  const queryFilter = {
    clinicGuid: filter.clinicGuid,
    parentClinicGuid: filter.parentClinicGuid,
    doctorGuid: filter.doctorGuid,
    status: filter.status,
    year: filter.year,
    month: filter.month,
  };

  const { data, isLoading, isFetching } = useQuery<
    ISuccessRateTiles | undefined,
    string | undefined
  >({
    queryKey: ['getPatientEngagementSuccessRate', queryFilter],
    queryFn: async () => {
      const { data: response, error } =
        await dataService.getList<ISuccessRateTiles>(
          apiUrlGenerator(url, queryFilter),
        );
      if (error) return Promise.reject(error);
      return response;
    },
    keepPreviousData: true,
  });

  return {
    tiles: data ?? emptyTiles(),
    isLoading: isLoading || isFetching,
  };
};
